import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

/**
 * 確認ダイアログ（2026-10-03・UX 監査 A-13）。ブラウザ標準の confirm() と、画面ごとに作っていた確認画面の代わり。
 *
 * - 危険度（danger: true）なら実行ボタンを赤にし、「元に戻せません」などの注意を出す。通常は青
 * - 実行ボタンは 1 回押したら閉じる（二度押しで 2 回実行しない）。0.6 秒以内のもう 1 回は無視する
 * - キャンセルが最初にフォーカスされる（Enter の押し間違いで実行しない）。外側（黒い部分）を押しても閉じない
 *
 * Radix（shadcn）の AlertDialog で作る。Chakra の AlertDialog だと、Radix の Dialog（従業員の詳細・シフトテンプレートの編集など）
 * の中から開いたとき、Radix が body に付ける pointer-events: none を受け継いで、表示はされるのにボタンが押せなかった。
 * Radix 同士なら、重なったときの押せる層・Esc・フォーカスを Radix が一番上の層に渡す。
 * Chakra の Modal（伝票のポップアップなど）の上で開いたときは、Chakra のフォーカス固定が確認ダイアログからフォーカスを
 * 引き戻さないよう data-no-focus-lock を付け、Chakra の Modal（z-index 1400）より前に出す。
 *
 * 使い方: `const confirm = useConfirm(); if (!(await confirm({ title: '…', description: '…', danger: true }))) return;`
 */
export type ConfirmOptions = {
  title: string;
  description?: React.ReactNode;
  /** 実行ボタンの文言（既定は「OK」。危険な操作は「削除する」など、何が起きるかが分かる言葉にする） */
  confirmLabel?: string;
  cancelLabel?: string;
  /** 取り消せない・お金や記録が消える操作 */
  danger?: boolean;
};

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<(ConfirmOptions & { open: boolean }) | null>(null);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);
  const openedAtRef = useRef(0);
  // 閉じた後にフォーカスを戻す先（確認を開いたボタン）。Trigger を使わないので Radix は戻し先を知らない
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const confirm = useCallback<ConfirmFn>((options) => new Promise<boolean>((resolve) => {
    // 前の確認が開いたままなら、そちらは取りやめにする
    resolverRef.current?.(false);
    resolverRef.current = resolve;
    openedAtRef.current = Date.now();
    returnFocusRef.current = typeof document !== 'undefined' && document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setState({ ...options, open: true });
  }), []);

  const finish = (ok: boolean) => {
    if (ok && Date.now() - openedAtRef.current < 600) return; // 開いた直後のもう 1 回（ダブルタップ）は無視する
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState((s) => (s ? { ...s, open: false } : s));
    resolve?.(ok);
  };

  const hasDescription = !!state?.description || !!state?.danger;

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {/* キャンセル（ボタン・Esc）はここに来る。外側を押しても AlertDialog は閉じない */}
      <AlertDialogPrimitive.Root open={!!state?.open} onOpenChange={(open) => { if (!open) finish(false); }}>
        <AlertDialogPrimitive.Portal>
          <AlertDialogPrimitive.Overlay
            className="fixed inset-0 z-[1450] bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          />
          <AlertDialogPrimitive.Content
            data-no-focus-lock=""
            data-testid="confirm-dialog"
            onCloseAutoFocus={(e) => {
              // 閉じたら確認を開いたボタンへ戻す（行ごと消えたなどで無ければ何もしない）
              e.preventDefault();
              const el = returnFocusRef.current;
              returnFocusRef.current = null;
              if (el && el.isConnected) el.focus();
            }}
            {...(hasDescription ? {} : { 'aria-describedby': undefined })}
            className="fixed left-1/2 top-1/2 z-[1450] grid w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 gap-4 rounded-lg border bg-background p-6 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          >
            <AlertDialogPrimitive.Title className={cn('text-lg font-bold', state?.danger && 'text-red-700 dark:text-red-400')}>
              {state?.title}
            </AlertDialogPrimitive.Title>
            {hasDescription && (
              <AlertDialogPrimitive.Description asChild>
                <div className="text-sm text-foreground">
                  {typeof state?.description === 'string'
                    ? <p className="whitespace-pre-wrap">{state.description}</p>
                    : state?.description}
                  {state?.danger && (
                    <p className="mt-2 font-semibold text-red-600 dark:text-red-400">この操作は元に戻せません。</p>
                  )}
                </div>
              </AlertDialogPrimitive.Description>
            )}
            <div className="flex flex-col gap-2">
              <Button
                variant={state?.danger ? 'destructive' : 'default'}
                className={cn('h-auto w-full whitespace-normal py-3', !state?.danger && 'bg-blue-600 text-white hover:bg-blue-700')}
                onClick={() => finish(true)}
              >
                {state?.confirmLabel ?? 'OK'}
              </Button>
              {/* Cancel にしておくと、開いたときに最初にフォーカスされる */}
              <AlertDialogPrimitive.Cancel asChild>
                <Button variant="ghost" className="w-full">
                  {state?.cancelLabel ?? 'キャンセル'}
                </Button>
              </AlertDialogPrimitive.Cancel>
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Portal>
      </AlertDialogPrimitive.Root>
    </ConfirmContext.Provider>
  );
}

/** 確認ダイアログを開く関数（ConfirmProvider の中で使う。外ではブラウザ標準の confirm に戻す） */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  return ctx ?? (async (o: ConfirmOptions) => (typeof window !== 'undefined'
    // eslint-disable-next-line no-restricted-properties -- Provider の外だけの予備
    ? window.confirm([o.title, typeof o.description === 'string' ? o.description : ''].filter(Boolean).join('\n'))
    : false));
}
